import { useRef, useCallback, useEffect, useState } from 'react';
import { Button, Space, notification, Modal, Input, Form } from 'antd';
import {
  SaveOutlined,
  ClearOutlined,
  DownloadOutlined,
  UploadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  OneToOneOutlined,
  DragOutlined,
} from '@ant-design/icons';
import WorkflowNode from '../WorkflowNode/WorkflowNode';
import Connection from './Connection';
import { exportWorkflow, importWorkflow } from '../../utils/sandbox/sandboxUtils';
import { t } from '../../i18n/i18n';

const Canvas = ({ sandbox }) => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saveForm] = Form.useForm();
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [handToolActive, setHandToolActive] = useState(false);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      const rect = canvasRef.current.getBoundingClientRect();
      const position = {
        x: (e.clientX - rect.left) / sandbox.zoom - sandbox.canvasOffset.x,
        y: (e.clientY - rect.top) / sandbox.zoom - sandbox.canvasOffset.y,
      };

      try {
        const template = JSON.parse(e.dataTransfer.getData('application/json'));
        sandbox.addNode(template, position);
        notification.success({
          message: `${t('ALKYMATION_NODE_ADDED')}: "${t(template.name)}"`,
          placement: 'topRight',
        });
      } catch (error) {
        notification.error({ message: t('ALKYMATION_ERROR_ADDING_NODE'), placement: 'topRight' });
      }
    },
    [sandbox]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleCanvasClick = useCallback(
    (e) => {
      if (
        !isPanning &&
        (e.target === canvasRef.current || e.target.classList.contains('canvas-background'))
      ) {
        sandbox.setSelectedNodeId(null);
      }
    },
    [sandbox, isPanning]
  );

  const handlePanStart = useCallback(
    (e) => {
      // Don't start panning if clicking on a node
      const isNodeElement =
        e.target.closest('.workflow-node') ||
        e.target.closest('.ant-card') ||
        e.target.classList.contains('connection-point');

      if (isNodeElement) {
        return;
      }

      // Middle mouse button (button 1) or hand tool active with left click
      if (e.button === 1 || (handToolActive && e.button === 0)) {
        e.preventDefault();
        setIsPanning(true);
        setPanStart({
          x: e.clientX - sandbox.canvasOffset.x,
          y: e.clientY - sandbox.canvasOffset.y,
        });
      }
    },
    [sandbox.canvasOffset, handToolActive]
  );

  const handlePanMove = useCallback(
    (e) => {
      if (isPanning) {
        e.preventDefault();
        const newOffset = {
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        };
        sandbox.setCanvasOffset(newOffset);
      }
    },
    [isPanning, panStart, sandbox]
  );

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleSave = () => {
    saveForm.setFieldsValue({
      name: sandbox.workflow.name || '',
      description: sandbox.workflow.description || '',
    });
    setSaveModalVisible(true);
  };

  const handleKeyDown = useCallback(
    async (e) => {
      if (e.key === 'Delete' && sandbox.selectedNodeId) {
        const wasActive = sandbox.workflow?.active && sandbox.workflow?._id;
        await sandbox.removeNode(sandbox.selectedNodeId);
        notification.info({ message: t('ALKYMATION_NODE_DELETED'), placement: 'topRight' });

        if (wasActive) {
          notification.warning({
            message: t('ALKYMATION_WORKFLOW_DEACTIVATED'),
            description: t('ALKYMATION_WORKFLOW_DEACTIVATED_BY_MODIFICATION'),
            placement: 'topRight',
          });
        }
      }

      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      }
    },
    [sandbox.selectedNodeId, sandbox]
  );

  const handleSaveConfirm = async () => {
    try {
      const values = await saveForm.validateFields();

      await sandbox.saveWorkflow(values.name, values.description);

      setSaveModalVisible(false);
      notification.success({ message: t('ALKYMATION_WORKFLOW_SAVED'), placement: 'topRight' });
    } catch (error) {
      if (error.errorFields) {
        notification.error({
          message: t('ALKYMATION_COMPLETE_REQUIRED_FIELDS'),
          placement: 'topRight',
        });
      } else if (
        error.code === 'WORKFLOW_REQUIRES_TRIGGER' ||
        error.message === 'WORKFLOW_REQUIRES_TRIGGER'
      ) {
        notification.warning({
          message: t('ALKYMATION_WORKFLOW_NEEDS_TRIGGER_TITLE'),
          description: t('ALKYMATION_WORKFLOW_NEEDS_TRIGGER_MESSAGE'),
          placement: 'topRight',
          duration: 5,
        });
      } else {
        notification.error({
          message: t('ALKYMATION_ERROR_SAVING_WORKFLOW'),
          placement: 'topRight',
        });
      }
    }
  };

  const handleSaveCancel = () => {
    setSaveModalVisible(false);
    saveForm.resetFields();
  };

  const handleClear = () => {
    sandbox.createNewWorkflow();
    notification.info({ message: t('ALKYMATION_CANVAS_CLEARED'), placement: 'topRight' });
  };

  const handleExport = () => {
    if (sandbox.workflow.nodes.length === 0) {
      notification.warning({
        message: t('ALKYMATION_EXPORT_EMPTY_WORKFLOW_WARNING'),
        placement: 'topRight',
      });
      return;
    }

    exportWorkflow(sandbox.workflow);
    notification.success({ message: t('ALKYMATION_WORKFLOW_EXPORTED'), placement: 'topRight' });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const importedWorkflow = await importWorkflow(file);
      const workspaceInfo = JSON.parse(localStorage.getItem('workspaceInfo'));

      if (importedWorkflow?.workspaceId !== workspaceInfo?.workspaceId) {
        notification.warning({
          message: t('ALKYMATION_WORKFLOW_NOT_IMPORTED_BY_WORKSPACE_TITLE'),
          description: `${t('ALKYMATION_WORKFLOW_NOT_IMPORTED_BY_WORKSPACE_DESCRIPTION')} ${
            workspaceInfo?.name
          }`,
          placement: 'topRight',
        });
        return;
      }

      sandbox.updateWorkflowState(importedWorkflow);
      notification.success({ message: t('ALKYMATION_WORKFLOW_IMPORTED'), placement: 'topRight' });
    } catch (error) {
      notification.error({
        message: `${t('ALKYMATION_ERROR_IMPORTING_WORKFLOW')}: ${error.message}`,
        placement: 'topRight',
      });
    }

    e.target.value = '';
  };

  const handleZoomIn = useCallback(() => {
    sandbox.setZoom((prevZoom) => Math.min(prevZoom + 0.1, 2));
  }, [sandbox]);

  const handleZoomOut = useCallback(() => {
    sandbox.setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  }, [sandbox]);

  const handleZoomReset = useCallback(() => {
    sandbox.setZoom(1);
    sandbox.setCanvasOffset({ x: 0, y: 0 });
    setHandToolActive(false);
  }, [sandbox]);

  const handleWheel = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        sandbox.setZoom((prevZoom) => Math.max(0.5, Math.min(2, prevZoom + delta)));
      }
    },
    [sandbox]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const canvasElement = canvasRef.current;
    if (canvasElement) {
      canvasElement.addEventListener('wheel', handleWheel, { passive: false });
      return () => canvasElement.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handlePanMove);
      document.addEventListener('mouseup', handlePanEnd);
      return () => {
        document.removeEventListener('mousemove', handlePanMove);
        document.removeEventListener('mouseup', handlePanEnd);
      };
    }
  }, [isPanning, handlePanMove, handlePanEnd]);

  return (
    <div className="canvas-container">
      <div className="canvas-toolbar">
        <Space>
          <Button className="canvas-btn" icon={<SaveOutlined />} onClick={handleSave}>
            {t('ALKYMATION_SAVE')}
          </Button>
          <Button className="canvas-btn" icon={<ClearOutlined />} onClick={handleClear}>
            {t('ALKYMATION_CLEAR')}
          </Button>
          <Button className="canvas-btn" icon={<DownloadOutlined />} onClick={handleExport}>
            {t('ALKYMATION_EXPORT')}
          </Button>
          <Button className="canvas-btn" icon={<UploadOutlined />} onClick={handleImportClick}>
            {t('ALKYMATION_IMPORT')}
          </Button>
        </Space>

        <Space>
          <Button
            className="canvas-btn"
            icon={<DragOutlined />}
            onClick={() => setHandToolActive(!handToolActive)}
            type={handToolActive ? 'primary' : 'default'}
          />
          <Button className="canvas-btn" icon={<ZoomOutOutlined />} onClick={handleZoomOut} />
          <Button className="canvas-btn" icon={<OneToOneOutlined />} onClick={handleZoomReset}>
            {Math.round(sandbox.zoom * 100)}%
          </Button>
          <Button className="canvas-btn" icon={<ZoomInOutlined />} onClick={handleZoomIn} />
        </Space>
      </div>

      <div
        ref={canvasRef}
        className="canvas-workspace"
        onMouseDown={handlePanStart}
        style={{ cursor: handToolActive ? 'grab' : isPanning ? 'grabbing' : 'default' }}
      >
        <div
          className="canvas-zoom-container"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={handleCanvasClick}
          style={{
            transform: `scale(${sandbox.zoom}) translate(${sandbox.canvasOffset.x}px, ${sandbox.canvasOffset.y}px)`,
            transformOrigin: 'top left',
          }}
        >
          <div className="canvas-background" />

          <svg className="connections-layer">
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#2196F3" />
              </marker>
            </defs>

            {sandbox.workflow?.connections?.map((connection) => (
              <Connection
                key={connection.id}
                connection={connection}
                nodes={sandbox.workflow.nodes}
                onDelete={async () => {
                  const wasActive = sandbox.workflow?.active && sandbox.workflow?._id;
                  await sandbox.removeConnection(connection.id);
                  notification.info({
                    message: t('ALKYMATION_CONNECTION_DELETED'),
                    placement: 'topRight',
                  });

                  if (wasActive) {
                    notification.warning({
                      message: t('ALKYMATION_WORKFLOW_DEACTIVATED'),
                      description: t('ALKYMATION_WORKFLOW_DEACTIVATED_BY_MODIFICATION'),
                      placement: 'topRight',
                    });
                  }
                }}
              />
            ))}

            {sandbox.tempConnection && (
              <Connection
                connection={sandbox.tempConnection}
                nodes={sandbox.workflow.nodes}
                temporary
              />
            )}
          </svg>

          {sandbox.workflow?.nodes?.map((node) => (
            <WorkflowNode
              key={node.id}
              node={node}
              selected={sandbox.selectedNodeId === node.id}
              disabled={isPanning}
              onSelect={() => sandbox.setSelectedNodeId(node.id)}
              onPositionChange={(position) => sandbox.updateNodePosition(node.id, position)}
              onDoubleClick={() => {
                sandbox.setSelectedNodeId(node.id);
                sandbox.setIsConfigModalOpen(true);
              }}
              onConnectionStart={(outputId) => {
                sandbox.setConnectionStart({ nodeId: node.id, outputId });
              }}
              onConnectionEnd={(inputId) => {
                if (sandbox.connectionStart) {
                  try {
                    sandbox.addConnection({
                      sourceNodeId: sandbox.connectionStart.nodeId,
                      sourceOutputId: sandbox.connectionStart.outputId,
                      targetNodeId: node.id,
                      targetInputId: inputId,
                    });
                    notification.success({
                      message: t('ALKYMATION_CONNECTION_CREATED'),
                      placement: 'topRight',
                    });
                  } catch (error) {
                    notification.error({ message: error.message, placement: 'topRight' });
                  }
                  sandbox.setConnectionStart(null);
                }
              }}
              onDelete={async () => {
                const wasActive = sandbox.workflow?.active && sandbox.workflow?._id;
                await sandbox.removeNode(node.id);
                notification.info({ message: t('ALKYMATION_NODE_DELETED'), placement: 'topRight' });

                if (wasActive) {
                  notification.warning({
                    message: t('ALKYMATION_WORKFLOW_DEACTIVATED'),
                    description: t('ALKYMATION_WORKFLOW_DEACTIVATED_BY_MODIFICATION'),
                    placement: 'topRight',
                  });
                }
              }}
              onDuplicate={() => sandbox.duplicateNode(node.id)}
            />
          ))}

          <div className="canvas-drop-zone">
            <div className="drop-zone-content">
              {!sandbox.workflow.nodes.length && <span>{t('ALKYMATION_DRAG_NODES_HERE')}</span>}
            </div>
          </div>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".json"
        onChange={handleFileImport}
      />

      <Modal
        title={t('ALKYMATION_SAVE_WORKFLOW_TITLE')}
        visible={saveModalVisible}
        onOk={handleSaveConfirm}
        onCancel={handleSaveCancel}
        okText={t('ALKYMATION_SAVE')}
        cancelText={t('ALKYMATION_CANCEL')}
      >
        <Form form={saveForm} layout="vertical" autoComplete="off">
          <Form.Item
            label={t('ALKYMATION_WORKFLOW_NAME')}
            name="name"
            rules={[
              { required: true, message: t('ALKYMATION_ENTER_WORKFLOW_NAME') },
              { max: 100, message: t('ALKYMATION_NAME_MAX_LENGTH') },
            ]}
          >
            <Input placeholder={t('ALKYMATION_WORKFLOW_NAME_PLACEHOLDER')} />
          </Form.Item>

          <Form.Item
            label={t('ALKYMATION_DESCRIPTION')}
            name="description"
            rules={[{ max: 500, message: t('ALKYMATION_DESCRIPTION_MAX_LENGTH') }]}
          >
            <Input.TextArea rows={4} placeholder={t('ALKYMATION_DESCRIPTION_PLACEHOLDER')} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Canvas;
